import React from 'react';
import { Navigate } from 'react-router-dom';
import type { CatalogImportMode } from '@/pages/catalogImport/store';
import { UploadStepView } from './UploadStepView';
import { useUploadStep, type UseUploadStepOptions } from './useUploadStep';

export type UploadStepProps = UseUploadStepOptions & {
    /**
     * Names Back: "← Branch" in the wizard, the exit Back "To Branches" on
     * the in-shell page (where Back is `/branches`).
     */
    mode: CatalogImportMode;
};

/**
 * Upload: the template, the drop zone and the file's first check. The wizard
 * mounts it at `/onboarding/file`; the in-shell import page mounts it with
 * the branch fixed. Renders inside a `CatalogImportProvider`.
 *
 * The import is for the store's branch, so a step that opens with none (the
 * store lives in memory, and a reload empties it) sends the user back to
 * choose one. That is decided once, as the step opens: when the store is
 * emptied later, Leave is already taking the user out of the flow, and a
 * navigation of the step's own would take its place.
 */
export const UploadStep: React.FC<UploadStepProps> = ({ mode, ...paths }) => {
    const { hasBranch, ...step } = useUploadStep(paths);
    const [openedWithoutBranch] = React.useState(!hasBranch);

    if (openedWithoutBranch) {
        return <Navigate replace to={paths.backPath} />;
    }

    return <UploadStepView mode={mode} {...step} />;
};
